/**
 * promo-progress.js — Locket Loves "3 for £45"
 *
 * Shapes already re-fetches its cart sections on every add and every quantity
 * change, and hands the fresh HTML to listeners. We ride that: no window.fetch
 * patching, no polling, no second add-to-cart path.
 *
 * Events used (all Shapes' own, see assets/theme-events.js and the Switch
 * developer docs at help.switchthemes.co/shapes/developer/theme-events):
 *
 *   window   shapes:cart:afteradditem   \
 *   window   shapes:cart:cartqtychange   >  carry detail.response.sections
 *   window   shapes:cart:update         /   including the freshly rendered
 *                                           `cart-items` section.
 *   document theme:cart:change              public aggregate event, used only
 *                                           as a safety net when no payload
 *                                           arrived.
 *
 * The module inside [data-cart-items] needs no help at all: Shapes runs
 * Alpine.morph over that subtree with the new server HTML, so it repaints
 * itself. This script exists for the copies that live outside the cart, i.e.
 * the one on the product page.
 */

(() => {
  'use strict';

  // The snippet renders once per placement, so this file can be requested more
  // than once on a page. Only ever wire up the listeners a single time.
  if (window.__promoProgressReady) return;
  window.__promoProgressReady = true;

  const ROOT_SELECTOR = 'promo-progress[data-promo-progress]';
  const CART_ROOT = '[data-cart-items]';
  const DISMISS_KEY = 'locketloves:promo-progress:dismissed';
  const DISMISS_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /* ------------------------------------------------------------------ utils */

  const parse = (html) =>
    new DOMParser().parseFromString(html, 'text/html');

  /** Every module that Shapes will NOT repaint for us. */
  const externalModules = () =>
    Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter(
      (el) => !el.closest(CART_ROOT)
    );

  const readState = (root) => {
    const tag = root.querySelector('script[type="application/json"][data-promo-state]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return null;
    }
  };

  /* ----------------------------------------------------------------- repaint */

  /**
   * Copy the meter values out of a freshly server-rendered module and into a
   * live one. Deliberately field by field: the product page module contains
   * hydrated <data-island> quick-buy components, and replacing markup would
   * tear them down and lose the shopper's variant selection.
   */
  const repaint = (target, fresh) => {
    const state = readState(fresh);
    const previous = Number(target.dataset.promoInSet || 0);

    // Slots. These are plain images and text, safe to swap wholesale.
    const freshSlots = fresh.querySelector('[data-promo-slots]');
    const targetSlots = target.querySelector('[data-promo-slots]');
    if (freshSlots && targetSlots) {
      targetSlots.innerHTML = freshSlots.innerHTML;
      targetSlots.setAttribute('aria-label', freshSlots.getAttribute('aria-label') || '');
      targetSlots.classList.toggle(
        'promo-progress__slots--complete',
        freshSlots.classList.contains('promo-progress__slots--complete')
      );

      // Nudge only the slot that just filled, and only if motion is welcome.
      if (state && !reduceMotion && state.inSet > previous) {
        const justFilled = targetSlots.children[state.inSet - 1];
        if (justFilled) {
          justFilled.classList.add('promo-progress__slot--just-filled');
          justFilled.addEventListener(
            'animationend',
            () => justFilled.classList.remove('promo-progress__slot--just-filled'),
            { once: true }
          );
        }
      }
    }

    // Text fields.
    const swapHTML = (selector) => {
      const a = fresh.querySelector(selector);
      const b = target.querySelector(selector);
      if (a && b) b.innerHTML = a.innerHTML;
    };

    swapHTML('[data-promo-message]');
    swapHTML('[data-promo-save]');
    swapHTML('[data-promo-ship-message]');
    swapHTML('[data-promo-cta-label]');

    // Call to action visibility.
    const freshCta = fresh.querySelector('[data-promo-cta]');
    const targetCta = target.querySelector('[data-promo-cta]');
    if (freshCta && targetCta) {
      targetCta.hidden = freshCta.hasAttribute('hidden');
    }

    // Free shipping bar.
    const freshFill = fresh.querySelector('[data-promo-ship-fill]');
    const targetFill = target.querySelector('[data-promo-ship-fill]');
    if (freshFill && targetFill) {
      targetFill.style.width = freshFill.style.width;
    }

    // Root state.
    target.classList.toggle('is-complete', fresh.classList.contains('is-complete'));
    if (state) {
      target.dataset.promoCount = state.count;
      target.dataset.promoInSet = state.inSet;
      target.dataset.promoComplete = String(state.complete);
    }

    const stateTag = target.querySelector('script[type="application/json"][data-promo-state]');
    const freshStateTag = fresh.querySelector('script[type="application/json"][data-promo-state]');
    if (stateTag && freshStateTag) stateTag.textContent = freshStateTag.textContent;

    trackCompletion(state);

    // Slot markup was just replaced, so rebuild the buttons on top of it.
    enhanceSlots(target);
    // The basket moved, so an earlier dismissal may no longer apply.
    applyDismissal(target);
  };

  /** Apply a cart-items HTML string to every module outside the cart. */
  const applyCartHTML = (html) => {
    const targets = externalModules();
    if (!targets.length || !html) return false;

    const fresh = parse(html).querySelector(ROOT_SELECTOR);
    if (!fresh) return false;

    targets.forEach((target) => repaint(target, fresh));
    return true;
  };

  /* ---------------------------------------------------------------- tracking */

  /**
   * Two independent channels, because they answer different questions.
   *
   * 1. Shopify.analytics.publish — the supported bridge from theme code into
   *    Web Pixels. The store's GA4 runs inside that sandbox, so theme JS cannot
   *    call gtag directly; a Custom Pixel subscribes to these names instead.
   *    See _promo/INSTALL.md section 6 for the pixel to paste in.
   *
   * 2. Cart attributes — written onto the cart, so they survive to the order.
   *    This is what actually answers "did the widget raise basket value",
   *    because you can segment orders on it in Shopify's own reports and
   *    exports without any analytics vendor at all.
   *
   * Everything here is best-effort. Tracking must never break the shop, so all
   * of it is wrapped and all of it fails silently.
   */

  const EVENT_PREFIX = 'promo_progress_';

  const track = (name, payload = {}) => {
    const body = { promotion: '3for', ...payload };
    try {
      window.Shopify?.analytics?.publish?.(EVENT_PREFIX + name, body);
    } catch (e) {
      /* pixel sandbox unavailable */
    }
    // Also a plain DOM event, so GTM or anything else can listen without
    // depending on Shopify's pixel layer.
    try {
      document.dispatchEvent(
        new CustomEvent(EVENT_PREFIX + name, { detail: body })
      );
    } catch (e) {
      /* nothing to do */
    }
  };

  /**
   * Merge attributes onto the cart. /cart/update.js merges rather than
   * replaces, so this cannot clobber attributes set by anything else. Keys are
   * underscore-prefixed, which keeps them out of the customer's view while
   * still landing on the order.
   *
   * This does not go through Shapes' cart JS, so it raises none of its events
   * and cannot start a repaint loop.
   */
  const setCartAttributes = (attributes) => {
    try {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        // This store navigates to /cart on add, which cancels in-flight
        // requests. keepalive lets the write complete after the page is gone.
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      return Promise.resolve();
    }
  };

  const session = {
    viewed: new Set(),
    pickerAdds: 0,
    lastComplete: null,
    firstSurface: null,
  };

  const noteSurface = (surface) => {
    if (session.firstSurface) return;
    session.firstSurface = surface;
    setCartAttributes({ _promo_surface: surface });
  };

  /** Fire once per role per page load. */
  const trackView = (root) => {
    const role = root.dataset.promoRole || 'unknown';
    if (session.viewed.has(role)) return;
    session.viewed.add(role);
    track('viewed', {
      surface: role,
      qualifying_count: Number(root.dataset.promoCount || 0),
      complete: root.dataset.promoComplete === 'true',
    });
  };

  /**
   * Watch for the set completing during this visit. Deliberately not fired for
   * someone who arrives with a full basket already: that is not the module
   * doing any work, and counting it would flatter the numbers.
   *
   * The baseline lives in sessionStorage rather than a variable, because this
   * store navigates to /cart on every add. An in-memory baseline would be
   * reseeded on each page load and the transition would never be seen.
   */
  const COMPLETE_KEY = 'locketloves:promo-progress:lastSets';

  const readLastSets = () => {
    try {
      const v = sessionStorage.getItem(COMPLETE_KEY);
      return v === null ? null : Number(v);
    } catch (e) {
      return null;
    }
  };

  const writeLastSets = (n) => {
    try {
      sessionStorage.setItem(COMPLETE_KEY, String(n));
    } catch (e) {
      /* storage blocked */
    }
  };

  const trackCompletion = (state) => {
    if (!state) return;
    const previous = readLastSets();
    writeLastSets(state.sets);

    // First sighting this session is the baseline, never an event.
    if (previous === null) return;
    // Only count sets that were newly completed here.
    if (state.sets <= previous) return;

    track('set_completed', {
      sets: state.sets,
      sets_added: state.sets - previous,
      qualifying_count: state.count,
      picker_adds: session.pickerAdds,
    });
    setCartAttributes({
      _promo_completed: 'true',
      _promo_sets: String(state.sets),
    });
  };

  /* ------------------------------------------------------- slots as buttons */

  /**
   * Turn empty slots into buttons that open the picker.
   *
   * Done here rather than in Liquid on purpose. The cart copy of the module has
   * no picker, and the repaint below copies slot markup from the cart render
   * into the product page copy. Enhancing after every paint keeps the two
   * concerns separate: Liquid always emits the same inert slots, and this makes
   * them interactive only where there is something for them to open.
   *
   * It also means no-JS shoppers get plain slots instead of dead buttons.
   */
  const enhanceSlots = (root) => {
    const picker = root.querySelector('[data-promo-picker]');
    if (!picker) return;

    const expanded = !picker.hidden;
    const empties = root.querySelectorAll('.promo-progress__slot--empty');

    empties.forEach((slot, i) => {
      if (slot.querySelector('[data-promo-slot-pick]')) {
        slot.querySelector('[data-promo-slot-pick]')
          .setAttribute('aria-expanded', String(expanded));
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'promo-progress__slot-btn';
      btn.setAttribute('data-promo-slot-pick', '');
      btn.setAttribute('aria-expanded', String(expanded));
      btn.setAttribute('aria-controls', picker.id);
      btn.innerHTML = slot.innerHTML;

      // A dashed box with a number in it does not read as a control. Give the
      // slot the same corner affordance the filled slots use for their tick.
      const plus = document.createElement('span');
      plus.className = 'promo-progress__add';
      plus.setAttribute('aria-hidden', 'true');
      plus.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
      btn.appendChild(plus);

      const label = document.createElement('span');
      label.className = 'sr-only';
      label.textContent = `Choose print ${i + 1}. Shows popular leggings in this offer.`;
      btn.appendChild(label);

      slot.replaceChildren(btn);
      slot.classList.add('promo-progress__slot--interactive');
    });
  };

  /* ------------------------------------------------------------------ picker */

  let lastOpener = null;

  const setPicker = (root, open) => {
    const picker = root.querySelector('[data-promo-picker]');
    if (!picker) return;

    picker.hidden = !open;
    root
      .querySelectorAll('[data-promo-slot-pick]')
      .forEach((b) => b.setAttribute('aria-expanded', String(open)));

    if (open) {
      const heading = picker.querySelector('[data-promo-picker-heading]');
      if (heading) heading.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-promo-slot-pick]');
    if (opener) {
      const root = opener.closest(ROOT_SELECTOR);
      const picker = root && root.querySelector('[data-promo-picker]');
      if (!picker) return;
      event.preventDefault();
      lastOpener = opener;
      const opening = picker.hidden;
      setPicker(root, opening);
      if (opening) {
        track('picker_opened', {
          qualifying_count: Number(root.dataset.promoCount || 0),
        });
      }
      return;
    }

    const cta = event.target.closest('[data-promo-cta] a');
    if (cta) {
      const root = cta.closest(ROOT_SELECTOR);
      track('cta_clicked', {
        surface: root?.dataset.promoRole,
        qualifying_count: Number(root?.dataset.promoCount || 0),
        destination: cta.getAttribute('href'),
      });
      // Not prevented: the link must still navigate.
    }

    const dismiss = event.target.closest('[data-promo-dismiss]');
    if (dismiss) {
      event.preventDefault();
      const root = dismiss.closest(ROOT_SELECTOR);
      const count = root ? root.dataset.promoCount : '0';
      try {
        localStorage.setItem(
          DISMISS_KEY,
          JSON.stringify({ at: Date.now(), count: Number(count) })
        );
      } catch (e) {
        /* Storage blocked. Still hide it for this page view. */
      }
      track('dismissed', {
        surface: root?.dataset.promoRole,
        qualifying_count: Number(count),
      });

      // Hide every copy, not just the one that was clicked.
      document.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
        el.hidden = true;
      });
    }
  });

  // Escape closes the picker and returns focus where it came from.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const picker = event.target.closest('[data-promo-picker]');
    if (!picker || picker.hidden) return;
    const root = picker.closest(ROOT_SELECTOR);
    setPicker(root, false);
    if (lastOpener && lastOpener.isConnected) lastOpener.focus();
  });

  /* ---------------------------------------------------------------- dismissal */

  /**
   * Re-show the module when the qualifying count moves away from whatever it
   * was when the shopper dismissed it. At that point they are engaging with the
   * offer and the meter is progress information, not an advert.
   */
  const applyDismissal = (root) => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(DISMISS_KEY) || 'null');
    } catch (e) {
      return;
    }
    if (!stored) return;

    const stale = Date.now() - stored.at > DISMISS_TTL;
    const moved = String(stored.count) !== root.dataset.promoCount;

    if (stale || moved) {
      root.hidden = false;
      if (stale) {
        try {
          localStorage.removeItem(DISMISS_KEY);
        } catch (e) {
          /* nothing to do */
        }
      }
    } else {
      root.hidden = true;
    }
  };

  /* --------------------------------------------------- picker add attribution */

  /**
   * Caught on submit, in the capture phase, rather than waiting for Shapes'
   * post-add event. This store navigates to /cart on add, so the post-add event
   * may never be delivered. Submit always fires first.
   */
  let lastPickerSubmit = 0;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.closest || !form.closest('[data-promo-picker]')) return;

      lastPickerSubmit = Date.now();
      session.pickerAdds += 1;
      track('add_from_picker', { picker_adds: session.pickerAdds });
      setCartAttributes({ _promo_picker_adds: String(session.pickerAdds) });
      noteSurface('picker');
    },
    true
  );

  /* ------------------------------------------------------------------ events */

  let lastPayloadAt = 0;

  const onShapesPayload = (event) => {
    // Attribute the add before repainting, while originalTarget still points at
    // the control the shopper actually used.
    const origin = event?.detail?.originalTarget;
    if (origin && origin.closest) {
      const fromPicker =
        origin.closest('[data-promo-picker]') || Date.now() - lastPickerSubmit < 3000;
      // Picker adds are already counted by the submit handler above; only note
      // the surface for everything else.
      if (!fromPicker) {
        noteSurface(origin.closest(ROOT_SELECTOR) ? 'module' : 'page');
      }
    }

    const html = event?.detail?.response?.sections?.['cart-items'];
    if (!html) return;
    lastPayloadAt = Date.now();
    applyCartHTML(html);
  };

  // Primary path. Shapes has already paid for this network request, so riding
  // its payload costs us nothing.
  window.addEventListener('shapes:cart:afteradditem', onShapesPayload);
  window.addEventListener('shapes:cart:cartqtychange', onShapesPayload);
  window.addEventListener('shapes:cart:update', onShapesPayload);

  /**
   * Safety net. `theme:cart:change` is the documented public event and fires
   * after the cart sections have been swapped. If it arrives without one of the
   * payload events having fired just before it (a code path we have not seen,
   * or a future Shapes refactor), fetch the section ourselves. One request,
   * only when the free ride failed.
   */
  /* ------------------------------------------------------------------- init */

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      enhanceSlots(root);
      applyDismissal(root);
      if (!root.hidden) trackView(root);
    });
  };

  /**
   * Check completion from the server render on every page load, so the
   * transition is caught on the navigate-to-cart flow as well as the AJAX one.
   */
  const initCompletion = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (root) trackCompletion(readState(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initCompletion();
    });
  } else {
    init();
    initCompletion();
  }

  // The cart copy is re-rendered by Shapes' own Alpine.morph, which replaces
  // slot markup wholesale, so re-enhance after the drawer opens too.
  document.addEventListener('theme:cart-drawer:open', init);

  document.addEventListener('theme:cart:change', () => {
    if (Date.now() - lastPayloadAt < 1500) return;
    if (!externalModules().length) return;

    fetch(`${window.location.pathname}?sections=cart-items`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => applyCartHTML(data['cart-items']))
      .catch(() => {
        /* Progress display only. A failure here must never block the cart. */
      });
  });
})();
